import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  serverTimestamp,
  increment,
  deleteDoc
} from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from './config';
import {
  SupportTicket,
  TicketResponse,
  TicketStatus,
  TicketPriority,
  TicketType,
  TicketFilter,
  TicketStats,
  TicketSettings
} from '../../types/ticket.types';

// Generate unique ticket number
export const generateTicketNumber = async (): Promise<string> => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;
  
  // Get the counter for today
  const counterRef = doc(db, 'ticketCounters', dateStr);
  const counterDoc = await getDoc(counterRef);
  
  let sequenceNumber = 1;
  if (counterDoc.exists()) {
    sequenceNumber = (counterDoc.data().count || 0) + 1;
  }
  
  // Update counter
  await setDoc(counterRef, {
    count: sequenceNumber,
    date: Timestamp.now()
  }, { merge: true });
  
  const ticketNumber = `TICK-${dateStr}-${String(sequenceNumber).padStart(4, '0')}`;
  return ticketNumber;
};

// Create a new support ticket
export const createTicket = async (
  ticketData: Omit<SupportTicket, 'id' | 'ticketNumber' | 'createdAt' | 'updatedAt' | 'responseCount' | 'lastResponseIsStaff'>
): Promise<string> => {
  try {
    const ticketNumber = await generateTicketNumber();
    
    const newTicket = {
      ...ticketData,
      ticketNumber,
      status: TicketStatus.OPEN,
      responseCount: 0,
      lastResponseIsStaff: false,
      isArchived: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    const docRef = await addDoc(collection(db, 'supportTickets'), newTicket);
    
    // Log activity
    await addDoc(collection(db, 'activityLogs'), {
      userId: ticketData.userId,
      userEmail: ticketData.userEmail,
      action: 'create',
      entity: 'ticket',
      entityId: docRef.id,
      entityName: ticketNumber,
      timestamp: Timestamp.now(),
      status: 'success'
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

// Get ticket by ID
export const getTicketById = async (ticketId: string): Promise<SupportTicket | null> => {
  try {
    const ticketDoc = await getDoc(doc(db, 'supportTickets', ticketId));
    if (!ticketDoc.exists()) return null;
    
    return { id: ticketDoc.id, ...ticketDoc.data() } as SupportTicket;
  } catch (error) {
    console.error('Error getting ticket:', error);
    return null;
  }
};

// Get tickets with filters
export const getTickets = async (filter?: TicketFilter): Promise<SupportTicket[]> => {
  try {
    // Build query without isArchived filter initially
    let q = query(collection(db, 'supportTickets'));

    // Apply filters
    if (filter?.status && filter.status.length > 0) {
      q = query(q, where('status', 'in', filter.status));
    }
    if (filter?.priority && filter.priority.length > 0) {
      q = query(q, where('priority', 'in', filter.priority));
    }
    if (filter?.type && filter.type.length > 0) {
      q = query(q, where('type', 'in', filter.type));
    }
    if (filter?.assignedTo) {
      q = query(q, where('assignedTo', '==', filter.assignedTo));
    }
    if (filter?.userId) {
      q = query(q, where('userId', '==', filter.userId));
    }

    // Order by creation date
    q = query(q, orderBy('createdAt', 'desc'));
    
    const snapshot = await getDocs(q);
    const tickets = snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure attachments field is included even if undefined
          attachments: data.attachments || []
        } as SupportTicket;
      })
      .filter(ticket => !ticket.isArchived);
    
    // Apply search term filter if provided
    if (filter?.searchTerm) {
      const searchLower = filter.searchTerm.toLowerCase();
      return tickets.filter(ticket => 
        ticket.ticketNumber.toLowerCase().includes(searchLower) ||
        ticket.subject.toLowerCase().includes(searchLower) ||
        ticket.description.toLowerCase().includes(searchLower)
      );
    }
    
    return tickets;
  } catch (error) {
    console.error('Error getting tickets:', error);
    return [];
  }
};

// Get user's tickets (My Tickets).
//
// NOTE: no `isArchived != true` filter here. Firestore treats `!=` as a range
// filter, and combined with `orderBy('createdAt')` it demands a
// (userId, createdAt, isArchived) index that was never deployed — the query
// failed with FAILED_PRECONDITION for EVERY user and My Tickets showed
// "No tickets yet". Archived tickets are filtered in memory instead, on the
// existing (userId, createdAt) index. Errors are thrown so the page can show
// them rather than masquerading as an empty list.
export const getUserTickets = async (userId: string): Promise<SupportTicket[]> => {
  const q = query(
    collection(db, 'supportTickets'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
      attachments: doc.data().attachments || []
    }) as SupportTicket)
    .filter(ticket => ticket.isArchived !== true);
};

// Update ticket status
export const updateTicketStatus = async (
  ticketId: string,
  status: TicketStatus,
  updatedBy: string
): Promise<void> => {
  try {
    const updateData: any = {
      status,
      updatedAt: Timestamp.now()
    };
    
    if (status === TicketStatus.CLOSED) {
      updateData.closedAt = Timestamp.now();
    }
    
    await updateDoc(doc(db, 'supportTickets', ticketId), updateData);
    
    // Log activity
    await addDoc(collection(db, 'activityLogs'), {
      userId: updatedBy,
      action: 'update_status',
      entity: 'ticket',
      entityId: ticketId,
      changes: [{ field: 'status', oldValue: null, newValue: status }],
      timestamp: Timestamp.now(),
      status: 'success'
    });
  } catch (error) {
    console.error('Error updating ticket status:', error);
    throw error;
  }
};

// Assign ticket to staff
export const assignTicket = async (
  ticketId: string,
  assignedTo: string,
  assignedToName: string,
  assignedBy: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'supportTickets', ticketId), {
      assignedTo,
      assignedToName,
      assignedAt: Timestamp.now(),
      status: TicketStatus.IN_PROGRESS,
      updatedAt: Timestamp.now()
    });
    
    // Log activity
    await addDoc(collection(db, 'activityLogs'), {
      userId: assignedBy,
      action: 'assign',
      entity: 'ticket',
      entityId: ticketId,
      changes: [{ field: 'assignedTo', oldValue: null, newValue: assignedToName }],
      timestamp: Timestamp.now(),
      status: 'success'
    });
  } catch (error) {
    console.error('Error assigning ticket:', error);
    throw error;
  }
};

// Add response to ticket
export const addTicketResponse = async (
  ticketId: string,
  response: Omit<TicketResponse, 'id' | 'createdAt'>
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // Add response
    const responseRef = doc(collection(db, 'ticketResponses'));
    batch.set(responseRef, {
      ...response,
      createdAt: Timestamp.now()
    });
    
    // Update ticket
    const ticketUpdate: any = {
      responseCount: increment(1),
      lastResponseAt: Timestamp.now(),
      lastResponseIsStaff: response.isStaffResponse,
      updatedAt: Timestamp.now()
    };
    
    // Update status based on who responded
    if (response.isStaffResponse) {
      ticketUpdate.status = TicketStatus.ANSWERED;
    } else {
      ticketUpdate.status = TicketStatus.OPEN;
    }
    
    batch.update(doc(db, 'supportTickets', ticketId), ticketUpdate);
    
    await batch.commit();
  } catch (error) {
    console.error('Error adding ticket response:', error);
    throw error;
  }
};

// Get ticket responses
export const getTicketResponses = async (ticketId: string): Promise<TicketResponse[]> => {
  try {
    const q = query(
      collection(db, 'ticketResponses'),
      where('ticketId', '==', ticketId),
      orderBy('createdAt', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as TicketResponse[];
  } catch (error) {
    console.error('Error getting ticket responses:', error);
    return [];
  }
};

// Get ticket statistics
export const getTicketStats = async (filter?: TicketFilter): Promise<TicketStats> => {
  try {
    const tickets = await getTickets(filter);
    
    const stats: TicketStats = {
      total: tickets.length,
      open: tickets.filter(t => t.status === TicketStatus.OPEN).length,
      inProgress: tickets.filter(t => t.status === TicketStatus.IN_PROGRESS).length,
      answered: tickets.filter(t => t.status === TicketStatus.ANSWERED).length,
      closed: tickets.filter(t => t.status === TicketStatus.CLOSED).length,
      avgResponseTime: 0,
      avgResolutionTime: 0,
      satisfactionScore: 0
    };
    
    // Calculate average response time
    const responseTimes = tickets
      .filter(t => t.lastResponseAt)
      .map(t => {
        const created = t.createdAt.toMillis();
        const responded = t.lastResponseAt!.toMillis();
        return (responded - created) / (1000 * 60 * 60); // Convert to hours
      });
    
    if (responseTimes.length > 0) {
      stats.avgResponseTime = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
    }
    
    // Calculate average resolution time
    const resolutionTimes = tickets
      .filter(t => t.status === TicketStatus.CLOSED && t.closedAt)
      .map(t => {
        const created = t.createdAt.toMillis();
        const closed = t.closedAt!.toMillis();
        return (closed - created) / (1000 * 60 * 60); // Convert to hours
      });
    
    if (resolutionTimes.length > 0) {
      stats.avgResolutionTime = resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length;
    }
    
    // Calculate satisfaction score
    const ratings = tickets
      .filter(t => t.satisfactionRating)
      .map(t => t.satisfactionRating!);
    
    if (ratings.length > 0) {
      stats.satisfactionScore = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    }
    
    return stats;
  } catch (error) {
    console.error('Error getting ticket stats:', error);
    return {
      total: 0,
      open: 0,
      inProgress: 0,
      answered: 0,
      closed: 0,
      avgResponseTime: 0,
      avgResolutionTime: 0,
      satisfactionScore: 0
    };
  }
};

// Auto-close inactive tickets (to be called by scheduled function)
export const autoCloseInactiveTickets = async (): Promise<void> => {
  try {
    // Get settings
    const settingsDoc = await getDoc(doc(db, 'ticketSettings', 'default'));
    const settings = settingsDoc.exists() ? settingsDoc.data() as TicketSettings : null;
    const daysUntilClose = settings?.autoCloseAfterDays || 7;
    const warningDays = settings?.warningBeforeAutoCloseDays || 2;
    
    const now = Timestamp.now();
    const closeThreshold = new Date(now.toMillis() - (daysUntilClose * 24 * 60 * 60 * 1000));
    const warningThreshold = new Date(now.toMillis() - ((daysUntilClose - warningDays) * 24 * 60 * 60 * 1000));
    
    // Get tickets that need warning or closing
    const q = query(
      collection(db, 'supportTickets'),
      where('status', 'in', [TicketStatus.ANSWERED]),
      where('lastResponseIsStaff', '==', true)
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.docs.forEach(doc => {
      const ticket = doc.data() as SupportTicket;
      const lastResponseTime = ticket.lastResponseAt?.toMillis() || ticket.createdAt.toMillis();
      
      if (lastResponseTime < closeThreshold.getTime()) {
        // Auto-close ticket
        batch.update(doc.ref, {
          status: TicketStatus.CLOSED,
          closedAt: now,
          updatedAt: now
        });
      } else if (lastResponseTime < warningThreshold.getTime() && !ticket.autoCloseWarningAt) {
        // Send warning
        batch.update(doc.ref, {
          autoCloseWarningAt: now,
          updatedAt: now
        });
      }
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error auto-closing tickets:', error);
  }
};

// Get or create default ticket settings
export const getTicketSettings = async (): Promise<TicketSettings> => {
  try {
    const settingsDoc = await getDoc(doc(db, 'ticketSettings', 'default'));
    
    if (settingsDoc.exists()) {
      return { id: settingsDoc.id, ...settingsDoc.data() } as TicketSettings;
    }
    
    // Create default settings
    const defaultSettings: Omit<TicketSettings, 'id'> = {
      autoCloseAfterDays: 7,
      warningBeforeAutoCloseDays: 2,
      emailNotifications: false,
      notificationEmails: [],
      businessHours: {
        start: '08:00',
        end: '17:00',
        timezone: 'Africa/Johannesburg',
        workDays: [1, 2, 3, 4, 5] // Monday to Friday
      },
      responseTemplates: [],
      categories: ['General', 'Technical', 'Billing', 'Feature Request'],
      tags: [],
      updatedAt: Timestamp.now(),
      updatedBy: 'system'
    };
    
    await setDoc(doc(db, 'ticketSettings', 'default'), defaultSettings, { merge: true });
    
    return { id: 'default', ...defaultSettings };
  } catch (error) {
    console.error('Error getting ticket settings:', error);
    throw error;
  }
};

// Update ticket settings
export const updateTicketSettings = async (
  settings: Partial<TicketSettings>,
  updatedBy: string
): Promise<void> => {
  try {
    // Use setDoc with merge to create the document if it doesn't exist
    await setDoc(doc(db, 'ticketSettings', 'default'), {
      ...settings,
      updatedAt: Timestamp.now(),
      updatedBy
    }, { merge: true });
  } catch (error) {
    console.error('Error updating ticket settings:', error);
    throw error;
  }
};

// Archive a ticket
export const archiveTicket = async (
  ticketId: string,
  archivedBy: string
): Promise<void> => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);

    if (!ticketDoc.exists()) {
      throw new Error('Ticket not found');
    }

    await updateDoc(ticketRef, {
      isArchived: true,
      archivedAt: Timestamp.now(),
      archivedBy: archivedBy,
      status: TicketStatus.CLOSED,
      updatedAt: Timestamp.now()
    });

    // Log activity
    await addDoc(collection(db, 'activityLogs'), {
      userId: archivedBy,
      action: 'archive',
      entity: 'ticket',
      entityId: ticketId,
      entityName: ticketDoc.data().ticketNumber,
      timestamp: Timestamp.now(),
      status: 'success'
    });
  } catch (error) {
    console.error('Error archiving ticket:', error);
    throw error;
  }
};

// Restore an archived ticket
export const restoreTicket = async (
  ticketId: string,
  restoredBy: string
): Promise<void> => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);

    if (!ticketDoc.exists()) {
      throw new Error('Ticket not found');
    }

    await updateDoc(ticketRef, {
      isArchived: false,
      archivedAt: null,
      archivedBy: null,
      status: TicketStatus.OPEN,
      updatedAt: Timestamp.now()
    });

    // Log activity
    await addDoc(collection(db, 'activityLogs'), {
      userId: restoredBy,
      action: 'restore',
      entity: 'ticket',
      entityId: ticketId,
      entityName: ticketDoc.data().ticketNumber,
      timestamp: Timestamp.now(),
      status: 'success'
    });
  } catch (error) {
    console.error('Error restoring ticket:', error);
    throw error;
  }
};

// Delete ticket attachments from storage.
//
// Attachments are stored by their download URL on the ticket doc. The web
// form uploads to `tickets/attachments/<file>` (a flat folder keyed by
// timestamp, not ticket id), promoted items reuse the source screenshot's
// URL, and legacy tickets used `support-tickets/{ticketId}/`. Deleting by URL
// covers every layout; the legacy folder sweep is kept for old tickets.
// Best-effort: a missing object must never block the ticket delete.
const deleteTicketAttachments = async (
  ticketId: string,
  attachmentUrls: string[]
): Promise<number> => {
  let deleted = 0;
  for (const url of attachmentUrls) {
    // Only delete objects this app owns; promoted review-note screenshots
    // belong to the reviewNotes doc and are cleaned when that note is deleted.
    if (!/\/o\/(tickets|support-tickets)%2F/.test(url) && !/\/(tickets|support-tickets)\//.test(url)) {
      continue;
    }
    try {
      await deleteObject(ref(storage, url));
      deleted++;
    } catch (error) {
      if ((error as { code?: string }).code !== 'storage/object-not-found') {
        console.error('Error deleting ticket attachment:', url, error);
      }
    }
  }
  try {
    const legacyFolder = ref(storage, `support-tickets/${ticketId}`);
    const filesList = await listAll(legacyFolder);
    await Promise.all(filesList.items.map(item => deleteObject(item)));
    deleted += filesList.items.length;
  } catch (error) {
    if ((error as { code?: string }).code !== 'storage/object-not-found') {
      console.error('Error sweeping legacy ticket attachment folder:', error);
    }
  }
  console.log(`Deleted ${deleted} attachments for ticket ${ticketId}`);
  return deleted;
};

// Delete a ticket permanently
export const deleteTicket = async (
  ticketId: string,
  deletedBy: string
): Promise<void> => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);

    if (!ticketDoc.exists()) {
      console.warn(`Ticket ${ticketId} not found - may have already been deleted`);
      return; // Don't throw error if already deleted
    }

    const ticketData = ticketDoc.data();

    // Delete all responses
    const responsesQuery = query(
      collection(db, 'ticketResponses'),
      where('ticketId', '==', ticketId)
    );
    const responsesSnapshot = await getDocs(responsesQuery);

    const batch = writeBatch(db);
    responsesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete the ticket
    batch.delete(ticketRef);
    await batch.commit();

    // Delete attachments from storage (best-effort, never blocks)
    const attachmentsDeleted = await deleteTicketAttachments(
      ticketId,
      Array.isArray(ticketData.attachments) ? ticketData.attachments : []
    );

    // Log activity
    await addDoc(collection(db, 'activityLogs'), {
      userId: deletedBy,
      action: 'delete',
      entity: 'ticket',
      entityId: ticketId,
      entityName: ticketData.ticketNumber,
      timestamp: Timestamp.now(),
      status: 'success',
      metadata: {
        attachmentsDeleted,
        responsesDeleted: responsesSnapshot.size
      }
    });
  } catch (error) {
    console.error('Error deleting ticket:', error);
    throw error;
  }
};

// Get archived tickets
export const getArchivedTickets = async (filter?: TicketFilter): Promise<SupportTicket[]> => {
  try {
    // Use simpler query to avoid index requirement
    let q = query(
      collection(db, 'supportTickets'),
      where('isArchived', '==', true)
    );

    // Apply additional filters if provided
    if (filter?.userId) {
      q = query(q, where('userId', '==', filter.userId));
    }

    if (filter?.assignedTo) {
      q = query(q, where('assignedTo', '==', filter.assignedTo));
    }

    const snapshot = await getDocs(q);
    let tickets = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as SupportTicket[];

    // Sort by archivedAt in memory to avoid index requirement
    tickets.sort((a, b) => {
      const aTime = a.archivedAt?.toMillis() || 0;
      const bTime = b.archivedAt?.toMillis() || 0;
      return bTime - aTime; // Descending order
    });

    // Apply search filter in memory
    if (filter?.searchTerm) {
      const searchLower = filter.searchTerm.toLowerCase();
      return tickets.filter(ticket =>
        ticket.ticketNumber.toLowerCase().includes(searchLower) ||
        ticket.subject.toLowerCase().includes(searchLower) ||
        ticket.description.toLowerCase().includes(searchLower) ||
        ticket.userName.toLowerCase().includes(searchLower)
      );
    }

    return tickets;
  } catch (error) {
    console.error('Error getting archived tickets:', error);
    return [];
  }
};